import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import * as R from 'ramda';

import {
  INITIAL_IS_ASCENDING,
  INITIAL_LIMIT,
  INITIAL_PAGE,
  INITIAL_SORT_BY,
} from '@atom/components/common/requests/assetRequestConstants';
import {
  cleanFilters,
  getCustomValues,
  getSDDOTTypeChangeFilters,
} from '@atom/components/common/requests/customTenantUtilities';
import ViewRequestModal from '@atom/components/common/requests/viewRequest/ViewRequestModal';
import { GET_ASSET_REQUESTS } from '@atom/graph/assetRequest';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Progress } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  AssetRequestsConnection,
  AssetRequestsConnectionInput,
  AssetRequestsFilters,
  AssetRequestType,
} from '@atom/types/assetRequest';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import RequestsFilters from './requestsFilters/RequestsFilters';
import RequestsTable from './requestsTable/RequestsTable';
import RequestsContext from './RequestsContext';

import classes from './requests.module.css';

const RequestsPortal = () => {
  const [, { backDestination, navigate }] = useNavigation();
  const { initialFilters } = getCustomValues();

  const [page, setPage] = useState<number>(INITIAL_PAGE);
  const [limit, setLimit] = useState<number>(INITIAL_LIMIT);
  const [sortBy, setSortBy] = useState<string>(INITIAL_SORT_BY);
  const [isAscending, setIsAscending] = useState<boolean>(INITIAL_IS_ASCENDING);
  const [filters, setFilters] = useState<AssetRequestsFilters>({});
  const [filtersCart, setFiltersCart] = useState<AssetRequestsFilters>({});
  const [activeRequestId, setActiveRequestId] = useState<string>();

  const resetInitialValues = () => {
    setPage(INITIAL_PAGE);
    setLimit(INITIAL_LIMIT);
    setSortBy(INITIAL_SORT_BY);
    setIsAscending(INITIAL_IS_ASCENDING);
  };

  const resetFilters = () => {
    resetInitialValues();
    setFilters(initialFilters);
    setFiltersCart(initialFilters);
  };

  useEffect(() => {
    if (
      isNilOrEmpty(filters) &&
      isNilOrEmpty(filtersCart) &&
      !isNilOrEmpty(initialFilters)
    ) {
      resetFilters();
    }
  }, [filters, filtersCart, initialFilters]);

  const {
    data,
    loading: assetRequestsLoading,
    refetch: refetchAssetRequests,
  } = useQuery<
    { assetRequests: AssetRequestsConnection },
    { input: AssetRequestsConnectionInput }
  >(GET_ASSET_REQUESTS, {
    variables: {
      input: {
        page,
        limit,
        sortBy,
        isAscending,
        ...(R.pathOr([], ['schemaIds'], filters)[0] !== '' && {
          schemaIds: filters.schemaIds,
        }),
        ...R.omit(['schemaIds'], filters),
      },
    },
    fetchPolicy: 'network-only',
  });

  const navigateBack = (): void => {
    return navigate(backDestination('/inventory'));
  };

  // When type is changed, filters should be automatically applied.
  const changeType = (type: AssetRequestType) => {
    const updatedFilter = getSDDOTTypeChangeFilters(type);

    // Handles refetching if a new request is created without changing types.
    if (type === filters?.type) {
      refetchAssetRequests();
    }

    setFiltersCart(updatedFilter);
    setFilters(cleanFilters(updatedFilter));
  };

  const applyFilters = () => {
    resetInitialValues();
    setFilters(cleanFilters(filtersCart));
  };

  const totalCount = R.pathOr(0, ['assetRequests', 'totalCount'], data);
  const requests = R.pathOr([], ['assetRequests', 'assetRequests'], data);

  return (
    <RequestsContext.Provider
      value={{
        requests,
        totalCount,
        filters,
        filtersCart,
        setFiltersCart,
        page,
        setPage,
        limit,
        setLimit,
        sortBy,
        setSortBy,
        isAscending,
        setIsAscending,
        changeType,
        setActiveRequestId,
      }}
    >
      <>
        <div className={classes.headerContainer}>
          <div className={classes.headerButtons}>
            <IconButton>
              <Icon color={colors.neutral.white} onClick={navigateBack}>
                arrow_back
              </Icon>
            </IconButton>
            <div>Inventory Requests</div>
          </div>
        </div>
        <div className={classes.bodyContainer}>
          <div className={classes.leftBodyPaneFixed}>
            <RequestsFilters
              applyFilters={applyFilters}
              resetFilters={resetFilters}
            />
          </div>
          <div className={classes.rightBodyPaneFill}>
            {assetRequestsLoading ? (
              <div className={classes.progressContainer}>
                <Progress />
              </div>
            ) : (
              <RequestsTable />
            )}
          </div>
        </div>
        <ViewRequestModal
          activeRequestId={activeRequestId}
          setActiveRequestId={setActiveRequestId}
        />
      </>
    </RequestsContext.Provider>
  );
};

export default RequestsPortal;
